/**
 * O estado da tela do painel "Amigos e chat" (docs/spec/friends.md): aberto ou fechado, a aba, a
 * busca, os grupos recolhidos, as janelas de conversa e os rascunhos. Os dados (amigos, mensagens)
 * ficam no store; aqui, só a interface. Não importa o store: o store usa este.
 *
 * As janelas e o aberto/fechado sobrevivem a recarregar a página (nesta aba); os grupos recolhidos,
 * a fechar o navegador.
 */
import { create } from 'zustand';
import { maxWindows, withWindow, type ChatWindow } from './friendsModel.js';

export type FriendsTab = 'friends' | 'requests' | 'blocked';

type FriendsUi = {
  open: boolean;
  tab: FriendsTab;
  query: string;
  /** o nome no "Adicionar amigo" (Pedidos) */
  addName: string;
  collapsed: Record<string, boolean>;
  windows: ChatWindow[];
  drafts: Record<string, string>;
  /** a janela que acabou de ser pedida: a caixa de texto dela ganha o foco */
  focus: { userId: string; nonce: number } | null;
  setOpen: (open: boolean) => void;
  toggle: () => void;
  /** abre o painel numa aba; `addName` já preenche o "Adicionar amigo" */
  show: (tab: FriendsTab, addName?: string) => void;
  setTab: (tab: FriendsTab) => void;
  setQuery: (query: string) => void;
  setAddName: (name: string) => void;
  /** recolhe ou abre um grupo da lista */
  setGroupShut: (key: string, shut: boolean) => void;
  /** abre a conversa expandida e com o foco; `minimized`: aberta por uma mensagem que chegou */
  openWindow: (userId: string, options?: { minimized?: boolean; narrow?: boolean }) => void;
  setMinimized: (userId: string, minimized: boolean) => void;
  closeWindow: (userId: string) => void;
  setDraft: (userId: string, text: string) => void;
  /** saiu da conta: tudo volta ao começo */
  reset: () => void;
};

const SESSION_KEY = 'rpgdesk.friends';
const COLLAPSED_KEY = 'rpgdesk.friends.collapsed';

function load<T>(storage: () => Storage, key: string, fallback: T): T {
  try {
    const raw = storage().getItem(key);
    return raw ? ({ ...fallback, ...(JSON.parse(raw) as T) } as T) : fallback;
  } catch {
    return fallback;
  }
}

function save(storage: () => Storage, key: string, value: unknown): void {
  try {
    storage().setItem(key, JSON.stringify(value));
  } catch {
    // sem armazenamento (navegação privada): só não lembra
  }
}

const session = () => window.sessionStorage;
const local = () => window.localStorage;
const initial = load(session, SESSION_KEY, { open: false, windows: [] as ChatWindow[] });

export const useFriendsUi = create<FriendsUi>((set, get) => ({
  open: initial.open,
  tab: 'friends',
  query: '',
  addName: '',
  collapsed: load(local, COLLAPSED_KEY, {} as Record<string, boolean>),
  windows: Array.isArray(initial.windows) ? initial.windows.filter((w) => typeof w?.userId === 'string') : [],
  drafts: {},
  focus: null,
  setOpen: (open) => set({ open }),
  toggle: () => set({ open: !get().open }),
  show: (tab, addName) => set({ open: true, tab, ...(addName !== undefined ? { addName, query: '' } : {}) }),
  setTab: (tab) => set({ tab }),
  setQuery: (query) => set({ query }),
  setAddName: (addName) => set({ addName }),
  setGroupShut: (key, shut) => {
    const collapsed = { ...get().collapsed, [key]: shut };
    save(local, COLLAPSED_KEY, collapsed);
    set({ collapsed });
  },
  openWindow: (userId, options = {}) => {
    const max = maxWindows(window.innerWidth, options.narrow);
    set((state) => ({
      windows: withWindow(state.windows, userId, max, options.minimized),
      focus: options.minimized ? state.focus : { userId, nonce: (state.focus?.nonce ?? 0) + 1 },
    }));
  },
  setMinimized: (userId, minimized) => set((state) => ({ windows: state.windows.map((w) => (w.userId === userId ? { ...w, minimized } : w)) })),
  closeWindow: (userId) => set((state) => ({ windows: state.windows.filter((w) => w.userId !== userId) })),
  setDraft: (userId, text) => set((state) => ({ drafts: { ...state.drafts, [userId]: text } })),
  reset: () => set({ open: false, tab: 'friends', query: '', addName: '', windows: [], drafts: {}, focus: null }),
}));

// lembra as janelas abertas nesta aba (recarregar a página não fecha as conversas)
useFriendsUi.subscribe((state, before) => {
  if (state.open !== before.open || state.windows !== before.windows) save(session, SESSION_KEY, { open: state.open, windows: state.windows });
});
